import { Connection, PublicKey, TransactionInstruction } from "@solana/web3.js";
import {
  ACCOUNT_SIZE,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  createBurnInstruction,
  createTransferCheckedInstruction,
  getAssociatedTokenAddressSync,
  getMint,
} from "@solana/spl-token";
import type { Recipient } from "./csv";
import {
  RECIPIENTS_PER_TX_EXISTING,
  RECIPIENTS_PER_TX_NEW_ATA,
} from "@oxminter/shared/distribution";

export interface MintFacts {
  address: string;
  decimals: number;
  supplyRaw: string;
  mintAuthority: string | null;
  freezeAuthority: string | null;
}

export async function readMintFacts(conn: Connection, mint: PublicKey): Promise<MintFacts> {
  const m = await getMint(conn, mint, "confirmed", TOKEN_PROGRAM_ID);
  return {
    address: mint.toBase58(),
    decimals: m.decimals,
    supplyRaw: m.supply.toString(),
    mintAuthority: m.mintAuthority?.toBase58() ?? null,
    freezeAuthority: m.freezeAuthority?.toBase58() ?? null,
  };
}

export function ataFor(mint: PublicKey, owner: PublicKey): PublicKey {
  return getAssociatedTokenAddressSync(mint, owner, false, TOKEN_PROGRAM_ID);
}

export function burnIx(
  mint: PublicKey,
  owner: PublicKey,
  amountRaw: bigint
): TransactionInstruction {
  return createBurnInstruction(
    ataFor(mint, owner),
    mint,
    owner,
    amountRaw,
    [],
    TOKEN_PROGRAM_ID
  );
}

export interface PreparedBatch {
  /** Recipients in this batch, in order. */
  recipients: Recipient[];
  instructions: TransactionInstruction[];
  /** Recipients in this batch that need an ATA created (payer funds the rent). */
  newAccounts: number;
}

/** The limits live in shared/, so the console and the CLI cannot disagree. */
export { RECIPIENTS_PER_TX_NEW_ATA, RECIPIENTS_PER_TX_EXISTING };

/** Rent for one associated token account, read from the chain, not guessed. */
export async function ataRentLamports(conn: Connection): Promise<number> {
  return conn.getMinimumBalanceForRentExemption(ACCOUNT_SIZE, "confirmed");
}

/**
 * Which recipients have no account for this mint, and what the others already
 * hold.
 *
 * Chunked, so a thousand-wallet list costs ten RPC round trips rather than a
 * thousand. The balance comes free from the same read — and it is what lets a
 * resumed run tell "not paid yet" from "already paid by a run whose journal
 * was lost", which is the difference between a correct resume and paying a
 * thousand people twice.
 */
export async function findMissingAtas(
  conn: Connection,
  mint: PublicKey,
  recipients: Recipient[]
): Promise<{ missing: Set<string>; held: Map<string, bigint> }> {
  const missing = new Set<string>();
  const held = new Map<string, bigint>();
  const CHUNK = 100;
  for (let i = 0; i < recipients.length; i += CHUNK) {
    const slice = recipients.slice(i, i + CHUNK);
    const addrs = slice.map((r) => ataFor(mint, new PublicKey(r.address)));
    const infos = await conn.getMultipleAccountsInfo(addrs, "confirmed");
    infos.forEach((info, j) => {
      if (!info) {
        missing.add(slice[j].address);
        return;
      }
      if (info.data.length >= 72) {
        // SPL token account layout: amount is a little-endian u64 at offset 64.
        held.set(
          slice[j].address,
          new DataView(info.data.buffer, info.data.byteOffset + 64, 8).getBigUint64(0, true)
        );
      }
    });
  }
  return { missing, held };
}

export function planBatches(
  mint: PublicKey,
  sender: PublicKey,
  decimals: number,
  recipients: Recipient[],
  missingAtas: Set<string>
): PreparedBatch[] {
  const source = ataFor(mint, sender);
  const batches: PreparedBatch[] = [];

  let current: Recipient[] = [];
  let currentNew = 0;
  const capacity = () =>
    currentNew > 0 ? RECIPIENTS_PER_TX_NEW_ATA : RECIPIENTS_PER_TX_EXISTING;

  const flush = () => {
    if (!current.length) return;
    const instructions: TransactionInstruction[] = [];
    for (const r of current) {
      const owner = new PublicKey(r.address);
      const dest = ataFor(mint, owner);
      if (missingAtas.has(r.address)) {
        instructions.push(
          createAssociatedTokenAccountInstruction(sender, dest, owner, mint, TOKEN_PROGRAM_ID)
        );
      }
      instructions.push(
        createTransferCheckedInstruction(
          source,
          mint,
          dest,
          sender,
          r.raw,
          decimals,
          [],
          TOKEN_PROGRAM_ID
        )
      );
    }
    batches.push({ recipients: current, instructions, newAccounts: currentNew });
    current = [];
    currentNew = 0;
  };

  for (const r of recipients) {
    const isNew = missingAtas.has(r.address);
    const prospectiveNew = currentNew + (isNew ? 1 : 0);
    const limit = prospectiveNew > 0 ? RECIPIENTS_PER_TX_NEW_ATA : RECIPIENTS_PER_TX_EXISTING;
    if (current.length + 1 > limit) flush();
    current.push(r);
    if (isNew) currentNew++;
    if (current.length >= capacity()) flush();
  }
  flush();
  return batches;
}
